import React, { useMemo, useState } from 'react'
import { noop } from 'lodash'
import ConfirmationModal from '../overlays/confirmationModal'
import { useApplicationNotifications } from '../applicationNotifiers'
import type { ButtonProps } from '../elementals/button'
import type { SlottableComponent } from '../../types'

const DEFAULT_ERROR_TITLE = 'Sorry, an error occurred'
const DEFAULT_ERROR_MESSAGE =
  'Something went wrong. Please try again in a few moments.'

type ConfirmationProps = SlottableComponent<'actions'> & {
  close: () => void
  confirmButton: {
    onClick: () => Promise<void> | void
    label?: ButtonProps['label']
    role?: ButtonProps['role']
    disabled?: ButtonProps['disabled']
    isCountdownButton?: boolean
    reInitialize?: boolean
    preventClose?: boolean
  }
  cancelButton: {
    onClick: () => void
    label?: string
    disabled?: boolean
    preventClose?: boolean
  }
  formatConfirmError?: (err: unknown) => string
  onError?: (err: unknown) => void
  actionInfoText?: string
  /**
   * Custom modal body content. A string is rendered inside `Text.Body`;
   * any other node is rendered as-is. Passed through to the underlying
   * `ConfirmationModal`. Declared explicitly so callers can discover that
   * arbitrary content (e.g. controlled form fields) is supported.
   */
  children?: React.ReactNode
  preventClose?: boolean
  hideCancelButton?: boolean
  errorTitle?: string
  errorMessage?: string
}

const Confirmation: React.FC<ConfirmationProps> = ({
  cancelButton = {
    onClick: () => noop(),
  },
  confirmButton = {
    onClick: () => noop(),
  },
  close = () => noop(),
  onError = () => noop(),
  formatConfirmError,
  actionInfoText,
  preventClose = false,
  hideCancelButton = false,
  errorTitle = DEFAULT_ERROR_TITLE,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  children,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const notifications = useApplicationNotifications()

  const onCancelModal = () => {
    cancelButton.onClick()
    const preventToClose =
      typeof cancelButton.preventClose === 'boolean'
        ? cancelButton.preventClose
        : preventClose
    if (!preventToClose) {
      close()
    }
  }

  const onCloseModal = () => {
    cancelButton?.onClick()
    close()
  }

  const handleError = (err: unknown) => {
    const message =
      typeof formatConfirmError === 'function'
        ? formatConfirmError(err)
        : errorMessage
    notifications.toast?.open({
      role: 'error',
      title: errorTitle,
      message,
    })
  }

  const onConfirmModal = async () => {
    setIsLoading(true)
    try {
      await confirmButton.onClick()
      const preventToClose =
        typeof confirmButton.preventClose === 'boolean'
          ? confirmButton.preventClose
          : preventClose
      if (!preventToClose) {
        close()
      }
    } catch (err) {
      handleError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const cancelButtonProps = useMemo(
    () => ({
      ...cancelButton,
      label: cancelButton.label || 'Cancel',
      disabled: cancelButton.disabled || isLoading,
      onClick: onCancelModal,
    }),
    [cancelButton, onCancelModal]
  )

  const confirmButtonProps = useMemo(
    () => ({
      ...confirmButton,
      role: confirmButton.role || 'primary',
      label: (confirmButton.label as string | undefined) || 'Confirm',
      disabled: Boolean(confirmButton.disabled) || isLoading,
      loading: isLoading,
      onClick: onConfirmModal,
    }),
    [confirmButton, onConfirmModal]
  )

  return (
    <ConfirmationModal
      open
      onClose={onCloseModal}
      onError={onError}
      actionInfoText={actionInfoText}
      hideCancelButton={hideCancelButton}
      cancelButtonProps={cancelButtonProps}
      confirmButtonProps={confirmButtonProps}
      {...rest}
    >
      {children}
    </ConfirmationModal>
  )
}

export default Confirmation
